import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { ChatSession } from '../entities';
import { chatService } from '../services';
import { isAuthorizationError } from '../types/errors';
import { PAGINATION } from '../config/defaults';
import { isNativeApp } from '../utils/nativePlatform';

interface UseChatSessionsReturn {
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  currentSessionId: string | null;
  loadingSessions: boolean;
  pendingNewSession: boolean;
  hasMoreSessions: boolean;
  isLoadingMore: boolean;
  
  // Actions
  loadSessions: () => Promise<void>;
  loadMoreSessions: () => Promise<void>;
  createSession: () => Promise<ChatSession>;
  createAndSelectSession: () => Promise<ChatSession>;
  selectSession: (sessionId: string, isTyping?: boolean) => Promise<void>;
  startNewSession: () => void;
  deleteSession: (sessionId: string) => Promise<void>;
  clearSessions: () => Promise<void>;
  refreshSessions: () => Promise<void>;
  updateSessionFromSignalR: (data: any) => void;
  refreshSessionData: (sessionId: string) => Promise<void>;
}

export const useChatSessions = (): UseChatSessionsReturn => {
  const { isAuthenticated, user } = useAuth();
  
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [pendingNewSession, setPendingNewSession] = useState(false);
  const [hasMoreSessions, setHasMoreSessions] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [continuationToken, setContinuationToken] = useState<string | undefined>();
  const [authError, setAuthError] = useState<Error | null>(null);
  
  const SESSION_PAGE_SIZE = PAGINATION.CHAT_SESSIONS_PAGE_SIZE;

  // Throw authorization errors during render so they're caught by error boundary
  if (authError) {
    throw authError;
  }

  const selectSession = useCallback(async (sessionId: string, isTyping?: boolean) => {
    // Prevent session switching if agent is still typing/answering
    if (isTyping) {
      console.warn('[useChatSessions] Cannot switch sessions while agent is typing');
      return;
    }

    const session = sessions.find(s => s.id === sessionId) || null;
    setCurrentSession(session);
    setCurrentSessionId(sessionId);
    setPendingNewSession(false);
  }, [sessions]);

  const loadSessions = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;

    setLoadingSessions(true);
    try {
      const response = await chatService.getSessions(SESSION_PAGE_SIZE);
      const sessionInstances = response.sessions.map((session: any) => new ChatSession(session));
      
      const sortedSessions = sessionInstances.sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      
      setSessions(sortedSessions);
      setContinuationToken(response.continuationToken);
      setHasMoreSessions(!!response.continuationToken);
      
      // Check environment variable to determine whether to start with new session or load latest.
      // The native (iOS) app always opens a new chat on launch to match the web sign-in
      // experience, regardless of how the env flag resolves in the native build pipeline.
      const startWithNewSession =
        isNativeApp() || process.env.REACT_APP_START_WITH_NEW_SESSION === 'true';
      
      if (startWithNewSession) {
        // Start with new session regardless of existing sessions
        if (!currentSessionId && !pendingNewSession) {
          setPendingNewSession(true);
        }
      } else if (sortedSessions.length > 0 && !currentSessionId && !pendingNewSession) {
        // Default behavior: load latest session
        const latestSession = sortedSessions[0];
        // Set current session directly to avoid timing issues with selectSession
        setCurrentSession(latestSession);
        setCurrentSessionId(latestSession.id);
        setPendingNewSession(false);
      }
    } catch (error) {
      // Store authorization errors to be thrown during render
      if (isAuthorizationError(error)) {
        // Don't log auth errors - they're expected and handled by the error boundary
        setLoadingSessions(false);
        setAuthError(error as Error);
        return;
      }
      console.error('[useChatSessions] Failed to load sessions:', error);
      throw error;
    } finally {
      setLoadingSessions(false);
    }
  }, [isAuthenticated, user?.id, currentSessionId, pendingNewSession, SESSION_PAGE_SIZE]);

  const loadMoreSessions = useCallback(async () => {
    if (!isAuthenticated || !user?.id || !continuationToken || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await chatService.getSessions(SESSION_PAGE_SIZE, continuationToken);
      const sessionInstances = response.sessions.map((session: any) => new ChatSession(session));
      
      const sortedNewSessions = sessionInstances.sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      
      // Append new sessions to existing ones
      setSessions(prev => [...prev, ...sortedNewSessions]);
      setContinuationToken(response.continuationToken);
      setHasMoreSessions(!!response.continuationToken);
    } catch (error) {
      // Store authorization errors to be thrown during render
      if (isAuthorizationError(error)) {
        // Don't log auth errors - they're expected and handled by the error boundary
        setIsLoadingMore(false);
        setAuthError(error as Error);
        return;
      }
      console.error('[useChatSessions] Failed to load more sessions:', error);
      throw error;
    } finally {
      setIsLoadingMore(false);
    }
  }, [isAuthenticated, user?.id, continuationToken, isLoadingMore, SESSION_PAGE_SIZE]);

  const createSession = useCallback(async (): Promise<ChatSession> => {
    if (!isAuthenticated || !user?.id) {
      throw new Error('Not authenticated');
    }

    const sessionData = await chatService.createSession('New Chat');
    const session = new ChatSession(sessionData);
    setSessions(prev => [session, ...prev]);
    return session;
  }, [isAuthenticated, user?.id]);

  const createAndSelectSession = useCallback(async (): Promise<ChatSession> => {
    if (!isAuthenticated || !user?.id) {
      throw new Error('Not authenticated');
    }

    const sessionData = await chatService.createSession('New Chat');
    const session = new ChatSession(sessionData);
    
    // Update state atomically to ensure currentSession is immediately available
    setSessions(prev => [session, ...prev]);
    setCurrentSession(session);
    setCurrentSessionId(session.id);
    setPendingNewSession(false);
    
    return session;
  }, [isAuthenticated, user?.id]);

  const startNewSession = useCallback(() => {
    if (!isAuthenticated) {
      throw new Error('Not authenticated');
    }

    setCurrentSession(null);
    setCurrentSessionId(null);
    setPendingNewSession(true);
  }, [isAuthenticated]);

  const deleteSession = useCallback(async (sessionId: string) => {
    if (!isAuthenticated || !user?.id) {
      throw new Error('Not authenticated');
    }

    await chatService.deleteSession(sessionId);
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    
    if (currentSessionId === sessionId) {
      setCurrentSession(null);
      setCurrentSessionId(null);
    }
  }, [isAuthenticated, user?.id, currentSessionId]);

  const clearSessions = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      throw new Error('Not authenticated');
    }

    await Promise.all(sessions.map(session => chatService.deleteSession(session.id)));
    setSessions([]);
    setCurrentSession(null);
    setCurrentSessionId(null);
  }, [isAuthenticated, user?.id, sessions]);

  const refreshSessions = useCallback(async () => {
    // Reset pagination state and reload from beginning
    setContinuationToken(undefined);
    setHasMoreSessions(false);
    await loadSessions();
  }, [loadSessions]);

  const updateSessionFromSignalR = useCallback((data: any) => {
    if (!data?.sessionId) return;
    
    setSessions(prevSessions => 
      prevSessions.map(session => {
        if (session.id === data.sessionId) {
          const updated = new ChatSession({...session});
          
          if (data.title && data.title !== session.title) {
            updated.updateTitle(data.title);
          }
          
          if (data.quickQuestions) {
            updated.updateQuickQuestions(data.quickQuestions);
          }
          
          if (data.isProcessing !== undefined) {
            updated.updateProcessingState(data.isProcessing, data.isError || false);
          }
          
          if (data.metadata) {
            updated.updateMetadata(data.metadata);
          }
          
          return updated;
        }
        return session;
      })
    );
    
    setCurrentSession(prevSession => {
      if (prevSession && prevSession.id === data.sessionId) {
        const updated = new ChatSession({...prevSession});
        
        if (data.title && data.title !== prevSession.title) {
          updated.updateTitle(data.title);
        }
        
        if (data.quickQuestions) {
          updated.updateQuickQuestions(data.quickQuestions);
        }
        
        if (data.isProcessing !== undefined) {
          updated.updateProcessingState(data.isProcessing, data.isError || false);
        }
        
        if (data.metadata) {
          updated.updateMetadata(data.metadata);
        }
        
        return updated;
      }
      return prevSession;
    });
  }, []);

  const refreshSessionData = useCallback(async (sessionId: string) => {
    if (!isAuthenticated || !sessionId) return;

    try {
      console.log('[useChatSessions] Refreshing session data for:', sessionId);
      const updatedSession = await chatService.getSession(sessionId);
      
      // Update sessions list if session title or metadata changed
      setSessions(prevSessions => 
        prevSessions.map(session => {
          if (session.id === sessionId) {
            const hasChanges = session.title !== updatedSession.title || 
                              JSON.stringify(session.metadata) !== JSON.stringify(updatedSession.metadata);
                              
            if (hasChanges) {
              console.log('[useChatSessions] Session data updated:', {
                oldTitle: session.title,
                newTitle: updatedSession.title,
                oldMetadata: session.metadata,
                newMetadata: updatedSession.metadata
              });
              return updatedSession;
            }
          }
          return session;
        })
      );
      
      // Update current session if it's the same one
      setCurrentSession(prevSession => {
        if (prevSession?.id === sessionId) {
          const hasChanges = prevSession.title !== updatedSession.title || 
                            JSON.stringify(prevSession.metadata) !== JSON.stringify(updatedSession.metadata);
                            
          if (hasChanges) {
            console.log('[useChatSessions] Current session updated:', {
              oldTitle: prevSession.title,
              newTitle: updatedSession.title
            });
            return updatedSession;
          }
        }
        return prevSession;
      });
      
    } catch (error) {
      console.error('[useChatSessions] Failed to refresh session data:', error);
      // Don't throw - this is a fallback mechanism
    }
  }, [isAuthenticated]);

  return {
    sessions,
    currentSession,
    currentSessionId,
    loadingSessions,
    pendingNewSession,
    hasMoreSessions,
    isLoadingMore,
    
    loadSessions,
    loadMoreSessions,
    createSession,
    createAndSelectSession,
    selectSession,
    startNewSession,
    deleteSession,
    clearSessions,
    refreshSessions,
    updateSessionFromSignalR,
    refreshSessionData
  };
};
